import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@src/design-system/dialog';
import { AddSquare, CloseCircle, Edit2, Menu, Trash } from 'iconsax-react';
import { useState } from 'react';

import { Input } from '@src/design-system/input';
import { CircleCheck, LoaderCircle } from 'lucide-react';
import EditStatusInput from '../components/edit-status-input';
import { Button } from '@src/design-system/button';
import { DeleteConfirmDialog } from '@src/design-system/molecules/delete-confirm-dialog-button';

import { useOrderLeadReferals } from '@features/leads/services/order-lead-referal';
import { HasPermission } from '@features/roles/guards/has-permission';
import { entities } from '@shared/constants/entities';
import {
  Sortable,
  SortableContent,
  SortableItem,
  SortableItemHandle,
} from '@src/design-system/sortable';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import leadReferalApi from '@features/leads/services/lead-referal-api';
import { ReferalsList } from '@src/shared/components/referals-list';
import { cn } from '@src/design-system/helpers';

const ManageReferalDialog = ({
  withFullHeight = true,
}: {
  withFullHeight?: boolean;
}) => {
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [sourceValue, setSourceValue] = useState<string>('');
  const [editingId, setEditingId] = useState<number | null>(null);

  const { search } = useLocation();
  const queryParam = qs.parse(search, { ignoreQueryPrefix: true });
  const { data } = leadReferalApi.useFetchInfinite({
    name: queryParam.referal as string,
  });

  const referals = data?.pages.flatMap((p) => p.results);
  const totalReferalsCount = data?.pages[0].count;

  const {
    mutateAsync: createLeadReferal,
    isPending: createLeadReferalsPending,
  } = leadReferalApi.useCreate();

  const { mutateAsync: orderLeadReferal } = useOrderLeadReferals();

  const { mutateAsync: editLeadReferal, isPending: editLeadReferalsPending } =
    leadReferalApi.useUpdate();

  const {
    mutateAsync: deleteLeadReferal,
    isPending: deleteLeadReferalsPending,
  } = leadReferalApi.useDelete();

  const handleEditSource = (value: string) => {
    if (value && editingId) {
      editLeadReferal({ name: value, id: editingId }).then(() =>
        setEditingId(null)
      );
    }
  };

  const handleAddSource = () => {
    if (sourceValue) {
      createLeadReferal({ name: sourceValue }).then(() => {
        setSourceValue('');
        setAddSourceOpen(false);
      });
    }
  };

  return (
    <>
      <DialogHeader>Referrals Settings</DialogHeader>
      <DialogBody className='flex flex-col'>
        <div className='flex justify-between mb-2 font-medium items-center'>
          <span className='font-semibold text-sm text-gray-900'>
            Referrals ({totalReferalsCount})
          </span>
          <HasPermission
            entity={entities.leadReferal}
            action='add'
          >
            <span
              className='mr-2 cursor-pointer underline flex items-center gap-1 text-xs text-purple-900'
              onClick={() => setAddSourceOpen(true)}
            >
              <AddSquare size={16} /> Add Referral
            </span>
          </HasPermission>
        </div>

        <Sortable
          value={referals || []}
          onValueChange={(items) => {
            orderLeadReferal(items.map((i) => i.id));
          }}
          getItemValue={(referal) => referal.id}
        >
          <SortableContent className='flex-1'>
            {addSourceOpen && (
              <div className='bg-transparent hover:bg-transparent focus:bg-transparent mb-2'>
                <div className='flex gap-2 items-center w-full'>
                  <Input
                    size='sm'
                    className='w-full'
                    onChange={(e) => setSourceValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSource();
                      }
                    }}
                    suffexIcon={
                      <div className='text-black flex gap-1 items-center'>
                        <CloseCircle
                          onClick={() => setAddSourceOpen(false)}
                          size={16}
                          className='cursor-pointer opacity-80 hover:opacity-100'
                        />
                        <CircleCheck
                          onClick={() => handleAddSource()}
                          size={18}
                          className='cursor-pointer stroke-[1.5px] text-white fill-green-500'
                        />
                      </div>
                    }
                  />
                  {createLeadReferalsPending && (
                    <LoaderCircle
                      className='animate-spin'
                      size={20}
                    />
                  )}
                </div>
              </div>
            )}

            <div>
              <ReferalsList
                props={{
                  infiniteListProps: {
                    renderItem: (referal) => {
                      const id = referal.id;
                      const isEditing = editingId === id;
                      const name = referal.name;

                      return (
                        <SortableItem
                          className='group flex gap-2'
                          value={id}
                        >
                          <HasPermission
                            entity={entities.leadReferal}
                            action='change'
                          >
                            <SortableItemHandle
                              autoFocus={false}
                              asChild
                            >
                              <button>
                                <Menu
                                  size={20}
                                  variant='Bold'
                                  className='text-primary-500 shrink-0'
                                />
                              </button>
                            </SortableItemHandle>
                          </HasPermission>

                          <div className='flex items-center gap-2'>
                            <EditStatusInput
                              isEditing={isEditing}
                              onClose={() => setEditingId(null)}
                              value={name}
                              key={id}
                              editingId={id}
                              onConfirm={(value) => handleEditSource(value)}
                              isPending={editLeadReferalsPending}
                            />

                            {!isEditing && (
                              <div className='flex'>
                                <HasPermission
                                  entity={entities.leadReferal}
                                  action='change'
                                >
                                  <Button
                                    variant='ghost'
                                    size={'iconXs'}
                                    className='hidden group-hover:flex'
                                    onClick={() => setEditingId(id)}
                                  >
                                    <Edit2
                                      className='text-gray-800 cursor-pointer'
                                      size={20}
                                      variant='TwoTone'
                                    />
                                  </Button>
                                </HasPermission>

                                <HasPermission
                                  entity={entities.leadReferal}
                                  action='delete'
                                >
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button
                                        variant='ghost'
                                        size={'iconXs'}
                                      >
                                        <Trash
                                          size={20}
                                          color='#F05252'
                                          cursor='pointer'
                                          className='hidden group-hover:block'
                                        />
                                      </Button>
                                    </DialogTrigger>

                                    <DialogContent align='center'>
                                      <DeleteConfirmDialog
                                        key='lead source'
                                        title='Are you sure you want to delete this lead source?'
                                        isLoading={deleteLeadReferalsPending}
                                        onConfirm={(closeModal) =>
                                          deleteLeadReferal(
                                            { id },
                                            {
                                              onSuccess: closeModal,
                                            }
                                          )
                                        }
                                      />
                                    </DialogContent>
                                  </Dialog>
                                </HasPermission>
                              </div>
                            )}
                          </div>
                        </SortableItem>
                      );
                    },
                    props: {
                      listProps: { className: 'gap-2' },
                      listContainerProps: {
                        className: cn(
                          'flex-1 p-0',
                          withFullHeight ? 'max-h-full' : ''
                        ),
                      },
                      searchContainerProps: {
                        className: 'p-0',
                      },
                    },
                  },
                }}
              />
            </div>
          </SortableContent>
        </Sortable>
      </DialogBody>
    </>
  );
};

export default ManageReferalDialog;
